import isEqual from 'lodash/isEqual';
import { CreateActionParams } from '../actions';
import { PaymentMethod, PaymentOption } from '../../../../types/types';
import {
  isOwnPaymentParticipant,
  withParticipantPayment,
} from '../../../../utils/multiParticipantFlow/participantPayment';

export type DefaultPaymentOptionToApply = {
  selectedPaymentOption: PaymentOption;
  selectedPaymentType: PaymentMethod;
  // Multi-participant flow: apply to this participant's own selection (index > 0).
  participantIndex?: number;
};

// Keyed by slot identifier (plus `#p<index>` for participants after the first, so one
// call can carry several participants of the same slot).
export type SetDefaultPaymentOptions = (dataToUpdate: {
  [key: string]: DefaultPaymentOptionToApply;
}) => void;

export const defaultPaymentOptionKey = (
  slotIdentifier: string,
  participantIndex?: number,
) =>
  isOwnPaymentParticipant(participantIndex)
    ? `${slotIdentifier}#p${participantIndex}`
    : slotIdentifier;

export function createSetDefaultPaymentOptionsAction({
  getControllerState,
}: CreateActionParams): SetDefaultPaymentOptions {
  return async (dataToUpdate: {
    [key: string]: DefaultPaymentOptionToApply;
  }) => {
    const [state, setState] = getControllerState();
    const updatedSlotServices = { ...state.serviceData.slotServices };
    let isChanged = false;

    Object.keys(dataToUpdate).forEach((key) => {
      const { selectedPaymentOption, selectedPaymentType, participantIndex } =
        dataToUpdate[key];
      const serviceIdentifier = key.split('#p')[0];
      const slotService = updatedSlotServices[serviceIdentifier];

      if (isOwnPaymentParticipant(participantIndex)) {
        const current = slotService.participants?.[participantIndex]?.payment;
        if (
          !isEqual(current?.selectedPaymentOption, selectedPaymentOption) ||
          current?.selectedPaymentType !== selectedPaymentType
        ) {
          updatedSlotServices[serviceIdentifier] = {
            ...slotService,
            participants: withParticipantPayment(
              slotService.participants,
              participantIndex,
              { selectedPaymentOption, selectedPaymentType },
            ),
          };
          isChanged = true;
        }
        return;
      }

      // Only touch a slot whose selection actually differs, so callers can apply
      // defaults unconditionally without forcing a state update (and a re-render)
      // when nothing changed.
      if (
        !isEqual(slotService.selectedPaymentOption, selectedPaymentOption) ||
        slotService.selectedPaymentType !== selectedPaymentType
      ) {
        updatedSlotServices[serviceIdentifier] = {
          ...slotService,
          selectedPaymentOption,
          selectedPaymentType,
        };
        isChanged = true;
      }
    });

    if (isChanged) {
      setState({
        serviceData: {
          ...state.serviceData,
          slotServices: updatedSlotServices,
        },
      });
    }
  };
}
